import { Trans, useTranslation } from 'react-i18next';
import { BellExclamationDuotoneIcon } from '@c2fo/liquidity/icons';
import { useCurrencyContext } from '@/contexts/CurrencyContext';
import useGroupByBuyer from '@/enterprise/data/useGroupByBuyer';
import useTakerMarkets from '@/enterprise/data/useTakerMarkets';
import QueryBoundaries from '@/shared/components/QueryBoundaries';
import { asEnterpriseLink } from '@/utils/experienceLinkHelper';
import useLocaleFormat from '@/utils/useLocaleFormat';
import BuyerLogo from '../BuyerLogo';
import Widget, { WidgetSkeleton } from './Widget';

const OfferActivityWidgetComponent = () => {
  const { t } = useTranslation();
  const { selectedCurrency } = useCurrencyContext();
  const { asCurrency, asNumber, asPercent } = useLocaleFormat();
  const { data: takerMarkets } = useTakerMarkets();
  const buyers = useGroupByBuyer({ takerMarkets });

  // display max of 5 buyers with at least 1 division participating
  const firstFiveBuyers = buyers.filter((buyer) => buyer.stats.divisionsParticipating >= 1).slice(0, 5);
  const totalDivisionsParticipating = buyers.reduce((acc, buyer) => acc + buyer.stats.divisionsParticipating, 0);
  const totalDivisions = buyers.reduce((acc, buyer) => acc + buyer.stats.totalDivisionCount, 0);
  const totalDivisionsParticipatingString = `${asNumber({ value: totalDivisionsParticipating })}/${asNumber({
    value: totalDivisions,
  })} ${t('core.divisionsParticipating')}`;

  const items = firstFiveBuyers.map((buyer, idx) => {
    return {
      key: `${idx}`,
      content: (
        <div className="flex items-center gap-2">
          <div>
            <BuyerLogo buyerLogoUrl={buyer.makerOrganizationLogo} buyerName={buyer.makerOrganizationName} size="lg" />
          </div>
          <div className="truncate">
            <div className="truncate">{buyer.makerOrganizationName}</div>
            <div className="truncate font-serif text-2xl">
              {asCurrency(buyer.stats.estimatedEarlyPaymentAmount, selectedCurrency)}
            </div>
            <div className="truncate text-sm text-text-secondary">
              {`${asNumber({ value: buyer.stats.divisionsParticipating })}/${asNumber({
                value: buyer.stats.totalDivisionCount,
              })}`}{' '}
              {t('core.divisionsParticipating')}
            </div>
            {buyer.stats.divisionsParticipating > 0 && (
              <div className="mt-0.5 truncate text-xs">
                <span className="flex items-center gap-0.5 truncate">
                  {buyer.stats.acceptancePercentage >= 0.9 ? (
                    <Trans
                      i18nKey="core.estimatedAcceptanceWithPercentage"
                      components={{ style: <span className="font-medium text-green-700" /> }}
                      values={{ acceptancePercentage: asPercent({ value: buyer.stats.acceptancePercentage }) }}
                    />
                  ) : (
                    <>
                      <BellExclamationDuotoneIcon className="size-fit shrink-0 fill-red-700" />
                      <span className="truncate">
                        <Trans
                          i18nKey="core.estimatedAcceptanceWithPercentage"
                          components={{ style: <span className="font-medium text-red-700" /> }}
                          values={{ acceptancePercentage: asPercent({ value: buyer.stats.acceptancePercentage }) }}
                        />
                      </span>
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        </div>
      ),
      link: asEnterpriseLink(`offer-activity?customer=${buyer.makerOrganizationUuid}`),
    };
  });

  return (
    <Widget
      items={items}
      subtitle={totalDivisionsParticipatingString}
      title={t('core.offerActivity')}
      viewAllLink={asEnterpriseLink('offer-activity')}
      widgetType="offer-activity"
      zeroItemsMessage={t('homepage.offerActivityWidgetZeroItemsMessage')}
    />
  );
};

const OfferActivityWidget = () => (
  <QueryBoundaries LoadingComponent={() => <WidgetSkeleton />}>
    <OfferActivityWidgetComponent />
  </QueryBoundaries>
);

export default OfferActivityWidget;
